import React from 'react'
import { ListButton } from '../../components/simple/listButton'
import {
  IconArchived,
  IconAttendance,
  IconConference,
  IconDraft,
  IconInbox,
  IconScheduled,
  IconSent,
  IconSurvey,
  IconAssessment,
  IconFile,
} from '../../icons/library'
import { User } from '../../providers/context'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { useTranslation } from '../../providers/translation'
import { Divider } from '../../components/simple/divider'

export enum InboxTab {
  Inbox = 'inbox',
  Sent = 'sent',
  Draft = 'draft',
  Scheduled = 'scheduled',
  Attendance = 'attendance',
  Archived = 'archived',
  Conference = 'conference',
  Survey = 'form',
  Assessment = 'assessment',
}

export type InboxTabWithCount = Exclude<
  InboxTab,
  InboxTab.Sent | InboxTab.Archived | InboxTab.Attendance | InboxTab.Conference | InboxTab.Survey | InboxTab.Assessment
>

export type UnreadCounts = Record<InboxTabWithCount, number>
interface InboxTabsProps {
  tab: { tab: InboxTab }
  setTab: (value: InboxTab) => void
  user: User
  unreadCounts?: UnreadCounts
}

export const InboxTabs: React.FC<InboxTabsProps> = React.memo(({ tab, setTab, user, unreadCounts }) => {
  const { tr } = useTranslation()
  return (
    <div className="flex flex-col gap-2">
      <ListButton inset highlighted={tab.tab === InboxTab.Inbox} onClick={() => setTab(InboxTab.Inbox)}>
        <IconInbox className="mr-3 min-w-[17.5px]" /> {tr('Inbox')} <UnreadBadge count={unreadCounts?.inbox ?? 0} />
      </ListButton>
      <ListButton inset highlighted={tab.tab === InboxTab.Sent} onClick={() => setTab(InboxTab.Sent)}>
        <IconSent className="mr-3 min-w-[17.5px]" /> {tr('Sent')}
      </ListButton>
      <ListButton inset highlighted={tab.tab === InboxTab.Draft} onClick={() => setTab(InboxTab.Draft)}>
        <IconDraft className="mr-3 min-w-[17.5px]" /> {tr('Draft')} <UnreadBadge count={unreadCounts?.draft ?? 0} />
      </ListButton>
      <ListButton inset highlighted={tab.tab === InboxTab.Scheduled} onClick={() => setTab(InboxTab.Scheduled)}>
        <IconScheduled className="mr-3 min-w-[17.5px]" /> {tr('Scheduled')}{' '}
        <UnreadBadge count={unreadCounts?.scheduled ?? 0} />
      </ListButton>
      <ListButton inset highlighted={tab.tab === InboxTab.Archived} onClick={() => setTab(InboxTab.Archived)}>
        <IconArchived className="mr-3 min-w-[17.5px]" /> {tr('Archived')}
      </ListButton>
      <Divider className="hide-if-last !border-charcoal-hover" />
      {user.type === 'teacher' && user.showAttendance && (
        <ListButton inset highlighted={tab.tab === InboxTab.Attendance} onClick={() => setTab(InboxTab.Attendance)}>
          <IconAttendance className="mr-3 min-w-[17.5px]" /> {tr('Attendance')}
        </ListButton>
      )}
      {user.showPTCConferences && (
        <ListButton inset highlighted={tab.tab === InboxTab.Conference} onClick={() => setTab(InboxTab.Conference)}>
          <IconConference className="mr-3 min-w-[17.5px]" /> {tr('Conferences')}
        </ListButton>
      )}
      {user.showSurveys && (
        <ListButton inset highlighted={tab.tab === InboxTab.Survey} onClick={() => setTab(InboxTab.Survey)}>
          <IconSurvey className="mr-3 min-w-[17.5px]" /> {tr('Forms')}
        </ListButton>
      )}
      {user.showAssessments && (
        <ListButton inset highlighted={tab.tab === InboxTab.Assessment} onClick={() => setTab(InboxTab.Assessment)}>
          <IconFile className="mr-3 min-w-[17.5px]" /> {tr('Document Delivery')}
        </ListButton>
      )}
    </div>
  )
})

const UnreadBadge: React.FC<{ count: number }> = ({ count }) =>
  count > 0 && (
    <Badge badgeStyle={BadgeStyle.logo} className="ml-auto">
      {count}
    </Badge>
  )
